import { Typography } from "@mui/material";
import { DateTime } from "luxon";
import { memo } from "react";
import { InHouseTooltip } from "@/components/InHouseTooltip/InHouseTooltip";
import { FONT_SIZE, INK } from "@/theme/tokens";

const EMPTY_CELL = "--";

/**
 * The absolute, department-local submission time as shown in the cell (and fed to clipboard/CSV export
 * via `valueFormatter`). A nurse with no submission time renders a dash rather than an empty cell, so
 * the column reads as "none recorded" instead of blank.
 */
export const formatRequestedAt = (value: DateTime | null | undefined): string => (value ? value.toLocaleString(DateTime.DATETIME_MED) : EMPTY_CELL);

/**
 * The same timestamp without the year, for cells too narrow to hold one — a pickup's submission time
 * is read against an upcoming shift, so the year is almost always the current one and spending ~40px
 * on it truncates the clock time instead (which is the part that decides who applied first).
 *
 * Locale-aware Intl options rather than a hand-written `toFormat` pattern, so day/month order still
 * follows the viewer's locale like `DATETIME_MED` does. The relative form stays in the hover tooltip,
 * which is what disambiguates a timestamp from another year.
 */
const formatRequestedAtCompact = (value: DateTime | null | undefined): string =>
	value ? value.toLocaleString({ month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : EMPTY_CELL;

/**
 * Renders when a nurse submitted their shift-pickup request: the absolute localized timestamp in the
 * cell, with the relative form ("3 hours ago") on hover — the hover must ADD information, never repeat
 * the cell. An empty `title` is how MUI is told there is nothing to reveal, so a nurse with no
 * timestamp gets no tooltip rather than the visible dash echoed back.
 *
 * Promoted from `NursesMatrixGrid/columns.tsx` so the desktop approval grid and the minimized approval
 * popup render submission time identically (same precedent as `SeniorityCell`). Kept prop-driven with
 * no data-fetching so it stays a pure, shareable UIToolkit cell.
 */
export const RequestedAtCell = memo(function RequestedAtCell({
	requestedAt,
	cellTestId,
	compact = false,
}: {
	requestedAt: DateTime | null | undefined;
	cellTestId?: string;
	/** Drops the year (see `formatRequestedAtCompact`) for a narrow cell. Default false — wide grid columns keep it. */
	compact?: boolean;
}) {
	const relative = requestedAt?.toRelative();
	return (
		<InHouseTooltip title={relative ?? ""} arrow>
			<Typography
				data-testid={cellTestId}
				sx={{
					fontSize: FONT_SIZE.SECONDARY,
					color: requestedAt ? INK.PRIMARY : INK.SECONDARY,
					whiteSpace: "nowrap",
					overflow: "hidden",
					textOverflow: "ellipsis",
					maxWidth: "100%",
				}}
			>
				{compact ? formatRequestedAtCompact(requestedAt) : formatRequestedAt(requestedAt)}
			</Typography>
		</InHouseTooltip>
	);
});
